import { panel360Href } from '@/features/evaluations/lib/panel-360';
import type { NeedsActionItem, NeedsActionOverflow, SignoffResolvedUpdate, StaffUpdate } from '../types';

/**
 * Where each bell row takes the viewer (AC3a.4, AC3b.3, AC3c.4, AC4a.3,
 * AC4b.3). Every link is built here so a later slice can point conflicts at the
 * decision drawer by changing one function.
 */
export function bellHref(target: NeedsActionItem | StaffUpdate): string {
  if ('type' in target) {
    switch (target.type) {
      case 'conflicts.awaiting_decision':
        return pipelineHref(target.awardId, target.stageId, 'oversight');
      case 'approvals.awaiting_decision':
        // The approvals page keeps its selection in local state: no instance param.
        return '/approvals';
      case 'invitations.judges':
        return '/team';
    }
  }
  // A mid-round change (a judge added late or replaced, a substitute, a move)
  // opens the panel's History, where the change and its reason are recorded —
  // for a move, the panel the entry went to. With no panel, the round's Judging tab.
  if (
    target.kind === 'JUDGE_ADDED_LATE' ||
    target.kind === 'JUDGE_REPLACED' ||
    target.kind === 'ENTRY_SUBSTITUTE' ||
    target.kind === 'ENTRY_MOVED'
  ) {
    return target.panelId && target.stageId
      ? `${panel360Href(target.awardId, target.stageId, target.panelId)}?tab=history`
      : pipelineHref(target.awardId, target.stageId, 'work');
  }
  if (target.kind === 'SIGNOFF_RESOLVED') return signoffHref(target);
  // A round that went live, and a finished panel generation, both open the round's Judging tab.
  if (target.kind === 'JUDGING_STARTED' || target.kind === 'PANELS_GENERATED') {
    return pipelineHref(target.awardId, target.stageId, 'work');
  }
  switch (target.exportKind ?? 'APPLICATIONS') {
    case 'ROUND_RESULTS':
      return pipelineHref(target.awardId, target.stageId, 'results');
    case 'AWARD_CONFIG':
      return `/awards/${encodeURIComponent(target.awardId)}`;
    case 'APPLICATIONS':
      return `/applications?${new URLSearchParams({ award: target.awardId }).toString()}`;
    case 'PANEL_REPORT':
      // One panel's report opens that panel's page; the whole round's opens the
      // round's Reports -> Panels (without `report` the tab shows Judges first).
      return target.panelId && target.stageId
        ? panel360Href(target.awardId, target.stageId, target.panelId)
        : pipelineHref(target.awardId, target.stageId, 'reports', 'panels');
  }
}

/**
 * A decided sign-off opens where its action lives (#46 spec §11): a pass, the
 * screening round's workspace; a disqualify, or a pass spanning rounds, the
 * award's queue; a round event (open, close, decide, winners), the round, on
 * whichever tab it opens with (its sign-off note shows there). One that could
 * not be applied opens the award's approvals register instead.
 */
function signoffHref(target: SignoffResolvedUpdate): string {
  // Approved but not applied: nothing changed where the action lives, so the
  // place that shows the outcome — and why — is the award's approvals register.
  if (target.signoff?.outcome === 'STALE') {
    const params = new URLSearchParams({ awardId: target.awardId });
    if (target.stageId) params.set('stageId', target.stageId);
    params.set('tab', 'oversight');
    params.set('governance', 'register');
    return `/pipeline?${params.toString()}`;
  }
  const event = target.signoff?.event;
  if (event === 'application.disqualify' || (event === 'screening.pass' && !target.stageId)) {
    return `/applications?${new URLSearchParams({ award: target.awardId }).toString()}`;
  }
  if (event === 'screening.pass') return pipelineHref(target.awardId, target.stageId, 'work');
  return pipelineHref(target.awardId, target.stageId);
}

/** "and N more conflicts · open Oversight": the round of the first hidden conflict (AC3a.3). */
export function overflowHref(overflow: NeedsActionOverflow): string {
  if (!overflow.awardId) return '/pipeline';
  return pipelineHref(overflow.awardId, overflow.stageId, 'oversight');
}

function pipelineHref(
  awardId: string,
  stageId: string | null,
  tab?: 'oversight' | 'work' | 'results' | 'reports',
  report?: 'panels',
): string {
  const params = new URLSearchParams({ awardId });
  if (stageId) params.set('stageId', stageId);
  // No tab: the round opens on its default tab.
  if (tab) params.set('tab', tab);
  if (report) params.set('report', report);
  return `/pipeline?${params.toString()}`;
}
